/**
 * Reads a user-facing message from an HTTP error.
 * Prefer the API JSON `message` over Angular's
 * "Http failure response for https://…: 400 OK".
 */
export function getHttpErrorMessage(
  error: unknown,
  fallback = 'An error occurred'
): string {
  if (error == null) {
    return fallback;
  }
  if (typeof error === 'string') {
    return pickUserMessage(error) || fallback;
  }
  if (typeof error !== 'object') {
    return fallback;
  }

  const top = error as Record<string, unknown>;
  const fromBody = pickUserMessage(top['error']);
  if (fromBody) {
    return fromBody;
  }

  const fromTop = pickUserMessage(top['message']);
  if (fromTop) {
    return fromTop;
  }

  const status = typeof top['status'] === 'number' ? top['status'] : undefined;
  if (status === 0) {
    return 'Unable to connect. Please check your network.';
  }

  return fallback;
}

function isAngularHttpFailureMessage(msg: string): boolean {
  return (
    msg.startsWith('Http failure response for') ||
    msg.startsWith('Http failure during parsing')
  );
}

function pickUserMessage(value: unknown, depth = 0): string {
  if (value == null || depth > 3) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || isAngularHttpFailureMessage(trimmed)) {
      return '';
    }
    if (trimmed.startsWith('<!') || trimmed.toLowerCase().startsWith('<html')) {
      return '';
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return pickUserMessage(JSON.parse(trimmed), depth + 1);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (typeof value !== 'object') {
    return '';
  }

  const obj = value as Record<string, unknown>;
  for (const key of ['message', 'Message', 'title', 'Title', 'detail', 'Detail']) {
    const field = obj[key];
    if (typeof field === 'string') {
      const text = pickUserMessage(field, depth + 1);
      if (text) {
        return text;
      }
    }
  }

  if (obj['error'] !== value) {
    const nested = pickUserMessage(obj['error'], depth + 1);
    if (nested) {
      return nested;
    }
  }

  return '';
}
